// 🚪📷 Room-view math — the /room camera (ROOM_CAMERA_SPEC_2026_09_15 §4,
// full per-room recipes built 09-15 pm on Nina's "immersive and fun" ask).
// Pure functions only; every consumer is render-side.
// Gate: src/utils/roomView.test.js (alpha caps are anti-invariants there).
//
// ⚠️⚠️ THE STACKING TRUTH (learned the hard way, 09-15 am): these layers do
// NOT paint behind the page. Like AmbientLayer (see its §81-89 warning), the
// mount is a later DOM sibling of the page container, everything shares the
// root stacking context, and z:-1 paints UNDER the body wallpaper (i.e.
// invisible) — so every "backdrop" is really a translucent film OVER the
// chat text. The original porch (a 0.8-opacity sky gradient) fogged the
// whole page. Immersion therefore comes from SHAPE, color and the particle
// slot, never from opacity:
//   · 'film'   = may cover text anywhere          → opacity ≤ FILM_CAP
//   · 'accent' = shaped, fades to transparent     → opacity ≤ ACCENT_CAP
// (the 'dark' /scene wash at 0.30 is the loudest accepted precedent).
import { bandForHour } from './housePartOfDay';

export const FILM_CAP = 0.14;
export const ACCENT_CAP = 0.32;

// Sky gradient per band (the Outside Question mockup's palette, promoted).
// Keyed by band NAME so a Wave-1 band tuning pass carries through for free.
const BAND_SKY = {
  night:   ['#0b1026', '#1a2440'],
  dawn:    ['#2b2a4d', '#e08a5a'],
  morning: ['#8db4d6', '#cfe0ea'],
  midday:  ['#7fb0dd', '#bcd8ec'],
  golden:  ['#5b78b0', '#e2a44b'],
  dusk:    ['#232052', '#7a5a8e'],
};
const OUTDOORS = new Set(['roof', 'porch']);
const NIGHT_BANDS = new Set(['night', 'dusk']);
const DAYLIGHT_BANDS = new Set(['morning', 'midday']);
const EVENING_BANDS = new Set(['golden', 'dusk', 'night']);

const EAVE = '#140d06'; // porch wood shadow — the test pins this literal

// Layer builders. Opacity carries ALL the translucency (gradients run
// full-strength color → transparent), so the caps are honest.
const film = (bg, opacity) => ({ kind: 'film', bg, opacity: Math.min(opacity, FILM_CAP) });
const accent = (bg, opacity) => ({ kind: 'accent', bg, opacity: Math.min(opacity, ACCENT_CAP) });
const CLEAR = 'rgba(0,0,0,0)';
const pool = (x, y, w, h, color) => `radial-gradient(${w}% ${h}% at ${x}% ${y}%, ${color}, ${CLEAR} 70%)`;
const vignette = (color) => `radial-gradient(120% 120% at 50% 45%, ${CLEAR} 52%, ${color} 100%)`;
const topGlow = (color, to) => `linear-gradient(180deg, ${color}, ${CLEAR} ${to}%)`;
const bottomGlow = (color, to) => `linear-gradient(0deg, ${color}, ${CLEAR} ${to}%)`;
const shaft = (deg, color, from, mid, to) => `linear-gradient(${deg}deg, ${CLEAR} ${from}%, ${color} ${mid}%, ${CLEAR} ${to}%)`;

// ── Per-room recipes ────────────────────────────────────────────────────────
// Each is (band) → layers[]. Ingredients only: light pools, edge tints,
// shafts, vignettes — shaped to live at the page edges and corners where the
// wallpaper shows, feathered to near-nothing over the text columns.
const RECIPES = {
  dining: (band) => {
    const night = NIGHT_BANDS.has(band);
    return [
      accent(pool(50, 16, 52, 34, '#ffb45e'), night ? 0.22 : 0.14), // the pendant over the long table
      accent(bottomGlow('#7a4a1e', 12), 0.10),
      ...(night ? [accent(vignette('#140d06'), 0.16)] : []),
    ];
  },
  sunroom: (band) => {
    const night = NIGHT_BANDS.has(band);
    return night
      ? [
        accent(shaft(115, '#b9c6ff', 10, 26, 48), 0.10),            // moonlight through tall glass
        accent(vignette('#0d1020'), 0.16),
      ]
      : [
        accent(shaft(115, '#ffe9b0', 12, 30, 52), band === 'morning' ? 0.18 : 0.13),
        accent(pool(8, 100, 34, 26, '#7fb069'), 0.10),              // plants crowding the floor
        accent(pool(92, 100, 34, 26, '#7fb069'), 0.10),
      ];
  },
  library: (band) => {
    const night = NIGHT_BANDS.has(band);
    return [
      accent(pool(50, 38, 46, 36, '#e8a94e'), night ? 0.22 : 0.15), // the one warm lamp
      accent(vignette('#08070f'), night ? 0.26 : 0.20),             // shelves swallow the light
    ];
  },
  greenhouse: (band) => {
    const night = NIGHT_BANDS.has(band);
    return night
      ? [
        accent(topGlow('#16301e', 30), 0.16),
        accent(vignette('#0a1a10'), 0.18),
      ]
      : [
        accent(topGlow('#69b06e', 30), 0.12),                       // light through green glass
        accent(pool(6, 100, 40, 30, '#3f7a46'), 0.12),              // fronds at the edges
        accent(pool(94, 100, 40, 30, '#3f7a46'), 0.12),
        ...(band === 'morning' ? [film(`linear-gradient(180deg, #dfe8ef, ${CLEAR} 100%)`, 0.06)] : []),
      ];
  },
  bathroom: (band) => {
    const night = NIGHT_BANDS.has(band);
    return [
      accent(topGlow('#cfe0ea', 22), night ? 0.07 : 0.12),          // porcelain light
      accent(pool(50, 100, 60, 24, '#6ec4d8'), 0.10),               // tile glow
      ...(night ? [accent(vignette('#0a141a'), 0.15)] : []),
    ];
  },
  porch: (band) => {
    const [skyTop] = BAND_SKY[band] || BAND_SKY.dusk;
    return [
      accent(topGlow(EAVE, 13), 0.30),                              // the eave overhead
      accent(`linear-gradient(180deg, ${CLEAR} 3%, ${skyTop} 9%, ${skyTop} 18%, ${CLEAR} 30%)`,
        NIGHT_BANDS.has(band) ? 0.24 : 0.18),                       // sky in the gap under it
      accent(`linear-gradient(90deg, ${EAVE}, ${CLEAR} 7%), linear-gradient(270deg, ${EAVE}, ${CLEAR} 7%)`,
        0.16),                                                      // porch posts
    ];
  },
  roof: (band) => {
    const [top, bottom] = BAND_SKY[band] || BAND_SKY.dusk;
    return [
      film(`linear-gradient(180deg, ${top}, ${bottom})`, 0.12),     // open air, everywhere
      accent(topGlow(top, 36), NIGHT_BANDS.has(band) ? 0.28 : 0.24),// the sky owns the top
      ...(EVENING_BANDS.has(band) ? [accent(bottomGlow('#e8a94e', 10), 0.08)] : []), // town lights below
    ];
  },
  workshop: (band) => {
    const night = NIGHT_BANDS.has(band);
    return [
      accent(pool(30, 62, 44, 40, '#ffb45e'), night ? 0.20 : 0.15), // the bench bulb
      ...(DAYLIGHT_BANDS.has(band) ? [accent(pool(84, 4, 30, 18, '#9db8d6'), 0.10)] : []), // the window slit
      accent(vignette('#2a1c08'), 0.18),
    ];
  },
  attic: (band) => [
    accent(shaft(105, DAYLIGHT_BANDS.has(band) ? '#e8d9b0' : '#b9c6ff', 8, 22, 40),
      DAYLIGHT_BANDS.has(band) ? 0.11 : 0.08),                      // one dusty beam
    accent(vignette('#0d0805'), 0.26),                              // low dark rafters
  ],
  garage: (band) => {
    const night = NIGHT_BANDS.has(band);
    return [
      accent(topGlow('#dfe8ef', 9), night ? 0.20 : 0.16),           // the buzzing strip light
      film(`linear-gradient(180deg, #8fa8c8, ${CLEAR} 100%)`, 0.05),
      accent(vignette('#12161c'), night ? 0.18 : 0.13),
    ];
  },
  music: (band) => [
    accent(pool(50, 55, 42, 36, '#e8a94e'), NIGHT_BANDS.has(band) ? 0.14 : 0.10), // one waiting mic
    accent(vignette('#1a0d26'), 0.24),                              // velvet dark
  ],
  gym: (band) => {
    const night = NIGHT_BANDS.has(band);
    return [
      accent(topGlow('#f2ecd9', 18), night ? 0.06 : 0.10),
      film(`linear-gradient(180deg, #8fa8c8, ${CLEAR} 100%)`, 0.05),
      ...(night ? [accent(vignette('#141820'), 0.14)] : []),
    ];
  },
  office: (band) => {
    const night = NIGHT_BANDS.has(band);
    return [
      accent(pool(72, 55, 36, 32, '#ffd07a'), night ? 0.14 : 0.11), // the desk lamp
      accent(pool(22, 68, 30, 26, '#5eb8f2'), night ? 0.12 : 0.06), // a humming screen
      ...(night ? [accent(vignette('#0d1220'), 0.14)] : []),
    ];
  },
  den: (band) => {
    const lit = EVENING_BANDS.has(band);
    return [
      accent(pool(50, 96, 56, 42, '#ff8a3c'), lit ? 0.20 : 0.10),   // the hearth
      accent(vignette('#140d06'), lit ? 0.22 : 0.16),
    ];
  },
};

// A room the frontend doesn't know yet (backend registry grew first):
// a gentle lived-in dim, so a new door never renders naked.
const GENERIC = (band) => [
  accent(pool(50, 20, 50, 30, '#e8c98e'), NIGHT_BANDS.has(band) ? 0.14 : 0.10),
  accent(vignette('#100c14'), 0.18),
];

/** Backdrop recipe for a room view, or null (house = no backdrop; the banner
 *  art IS the main room's view — ruled 09-15). */
export function roomBackdrop(roomId, hour) {
  if (!roomId || roomId === 'house') return null;
  const band = bandForHour(hour) || bandForHour(21); // junk hour → dusk-ish, never a crash
  const layers = (RECIPES[roomId] || GENERIC)(band.name);
  return { band: band.name, layers };
}

// ── The one particle slot (lowest precedence) ───────────────────────────────
// liveScene > event > weather > THIS. Weather passes through (boosted
// low→med outdoors); otherwise each lived-in room breathes its own resting
// particles from AmbientLayer's EXISTING atmosphere vocabulary, band-gated.
// Streaks are ALWAYS false here — shooters are event-only (09-14 rule).
//
// 🪲 09-19 (Nina): FIREFLIES ON CLEAR NIGHTS in the sunroom, greenhouse and
// porch, April through October. "Clear" costs no code: weather already wins
// this slot, so a rain / storm / fog night shows weather and every other night
// shows fireflies. All three rooms had an EMPTY night slot (sunroom dust and
// greenhouse bloom are daylight looks, the porch had nothing), so nothing is
// displaced. Dusk counts as night: that is when they come out.
// 🪧 Same batch: the six rooms that had NO particles get one (each entry is a
// list now, first match wins; `months` = the house-day months it may run).
export const FIREFLY_MONTHS = [4, 5, 6, 7, 8, 9, 10];
const FIREFLIES = { atmosphere: 'fireflies', when: NIGHT_BANDS, months: FIREFLY_MONTHS };
const RESTING = {
  roof:       [{ atmosphere: 'stars',   when: NIGHT_BANDS }],
  bathroom:   [{ atmosphere: 'bubbles', when: null }],       // the tub is timeless
  den:        [{ atmosphere: 'embers',  when: EVENING_BANDS }],
  greenhouse: [{ atmosphere: 'bloom',   when: new Set(['morning', 'midday', 'golden']) }, FIREFLIES],
  sunroom:    [{ atmosphere: 'dust',    when: DAYLIGHT_BANDS }, FIREFLIES], // sunbeam motes
  porch:      [FIREFLIES],
  workshop:   [{ atmosphere: 'dust',    when: null }],
  attic:      [{ atmosphere: 'dust',    when: null }],
  library:    [{ atmosphere: 'dust',    when: null }],       // motes in the lamplight
  dining:     [{ atmosphere: 'embers',  when: EVENING_BANDS }], // the candles, and only once they are lit
  garage:     [{ atmosphere: 'dust',    when: null }],       // under the buzzing strip light
  gym:        [{ atmosphere: 'dust',    when: null }],       // chalk
  music:      [{ atmosphere: 'dust',    when: null }],
  office:     [{ atmosphere: 'dust',    when: null }],       // the desk lamp's pool
};

/** Month (1-12) of a house-day key, or null. A CONVERSION of the key the
 *  payload carries, never a clock read (one-clock doctrine). */
function monthOfDay(dayKey) {
  const m = /^\d{4}-(\d{2})-\d{2}$/.exec(String(dayKey || ''));
  return m ? Number(m[1]) : null;
}

/** The room's OWN resting look right now, or null. `dayKey` = the house day
 *  (circadian clock.day); a month-gated look never runs without one. */
function restingFor(roomId, hour, dayKey) {
  const list = RESTING[roomId];
  if (!list) return null;
  const band = bandForHour(hour);
  const month = monthOfDay(dayKey);
  const rest = list.find((r) => (!r.when || (band && r.when.has(band.name)))
    && (!r.months || (month != null && r.months.includes(month))));
  // resting:true keeps AmbientLayer's ~20fps resting cadence (08-14 perf
  // ruling): these near-invisible looks can run 24/7 in a parked tab, so
  // they must never buy the full-display-rate redraw a real /scene gets.
  return rest ? { atmosphere: rest.atmosphere, intensity: 'low', streaks: false, resting: true } : null;
}

export function roomSceneFallback(weatherLook, roomId, hour, dayKey) {
  const outdoors = OUTDOORS.has(roomId);
  if (weatherLook && weatherLook.atmosphere) {
    return {
      atmosphere: weatherLook.atmosphere,
      intensity: outdoors ? 'med' : 'low',
      streaks: false,
    };
  }
  return restingFor(roomId, hour, dayKey);
}

// ── 🪧 Particles live in the MARGINS (09-19, Nina: "page margins if it won't
// affect performance") ───────────────────────────────────────────────────────
// Over a room's real art the resting look was tuned for a blank wallpaper and
// reads as nothing. It may step up to 'med' ONLY when it can be kept off the
// chat column: the caller hands the reading lane's measured rect, and the
// particles are seeded outside it and fade to nothing over it (AmbientLayer's
// `field.lane`). Cost: none. Same canvas, same cadence, the count moves
// 12 → 22. No lane (a phone, a treated room, a window too narrow to HAVE
// margins) = exactly today's look.
export const MARGIN_MIN_PX = 90;   // a margin narrower than this is not a place to put anything
export function roomSceneInMargins(scene, laneRect, viewportW) {
  if (!scene || scene.resting !== true) return { scene: scene || null, field: null };
  const w = Number(viewportW);
  const left = laneRect ? Number(laneRect.left) - LANE_FEATHER : NaN;
  const right = laneRect ? Number(laneRect.left) + Number(laneRect.width) + LANE_FEATHER : NaN;
  if (!(w > 0) || !Number.isFinite(left) || !Number.isFinite(right)) return { scene, field: null };
  if (Math.max(left, w - right) < MARGIN_MIN_PX) return { scene, field: null };
  return { scene: { ...scene, intensity: 'med' }, field: { lane: { x0: left, x1: right } } };
}

// ── 🔍 Particles ON the painting (09-19: the peek covers the page's ambient
// canvas, z 5200 over z 0, so a room's particles could never show there) ─────
// In the peek they come from a PLACE in the picture. [x0, y0, x1, y1] in % of
// the painting: `box` = where they travel, `from` = where they are born
// (absent = anywhere in the box). Authored 09-19 against the real art on a 10%
// grid; like GERALD_HOTSPOTS one region serves every take, because the night /
// rain / season takes are anchored on the day composition.
export const PEEK_EMITTERS = {
  bathroom:   { bubbles:   { box: [60, 14, 93, 74], from: [63, 56, 90, 72] } },  // off the tub foam
  den:        { embers:    { box: [20, 24, 36, 68], from: [24, 55, 30, 66] } },  // the hearth
  dining:     { embers:    { box: [25, 34, 65, 60], from: [28, 52, 62, 58] } },  // the candles down the table
  library:    { dust:      { box: [26, 28, 70, 80] } },                          // lamp pool + the arched window
  attic:      { dust:      { box: [38, 5, 62, 88] } },                           // the one beam
  workshop:   { dust:      { box: [30, 12, 68, 58] } },                          // under the bench bulb
  roof:       { stars:     { box: [3, 3, 73, 30] } },                            // 🌕 09-26: the open sky above the hills, left of the chimney, never on the roof
  sunroom:    { dust:      { box: [35, 25, 85, 80] }, fireflies: { box: [22, 30, 98, 68] } }, // motes inside · fireflies beyond the glass
  greenhouse: { bloom:     { box: [10, 25, 92, 75] }, fireflies: { box: [6, 28, 94, 72] } },  // in among the fronds
  porch:      { fireflies: { box: [3, 38, 97, 80] } },                           // the yard and the water, never under the roof
  garage:     { dust:      { box: [30, 8, 70, 70] } },                           // under the strip light
  gym:        { dust:      { box: [38, 12, 62, 75] } },                          // the pendant's cone
  music:      { dust:      { box: [30, 25, 68, 75] } },                          // the window light
  office:     { dust:      { box: [34, 28, 60, 58] } },                          // the desk lamp's pool
};
export const PEEK_GAIN = 1.7; // louder than the room: no chat text sits under a painting

/** What the peek paints over `roomId`'s picture, or null. INDOORS the room's
 *  own look always shows (the tub bubbles whatever the sky is doing; rain
 *  falling across an indoor painting is the no-glass-rooms mistake). OUTDOORS
 *  weather wins, as it does in the room, and falls across the whole picture. */
export function roomPeekScene(weatherLook, roomId, hour, dayKey) {
  if (!roomId || roomId === 'house') return null;
  if (OUTDOORS.has(roomId) && weatherLook && weatherLook.atmosphere) {
    return { scene: { atmosphere: weatherLook.atmosphere, intensity: 'med', streaks: false, resting: true }, field: { gain: PEEK_GAIN, bare: true } };
  }
  const rest = restingFor(roomId, hour, dayKey);
  if (!rest) return null;
  const em = (PEEK_EMITTERS[roomId] || {})[rest.atmosphere] || null;
  return {
    scene: { ...rest, intensity: 'med' },
    field: { gain: PEEK_GAIN, bare: true, ...(em ? { box: em.box, ...(em.from ? { from: em.from } : {}) } : {}) },
  };
}

// ── Entry flavor (the toast line after a move; player copy, repo rule 9) ────
const ENTRY_LINES = {
  dining: 'Long table, low pendant light.',
  sunroom: 'Warm light through tall glass.',
  library: 'Lamplight and tall shelves.',
  greenhouse: 'Green air under misted glass.',
  bathroom: 'Cold tile, honest mirror.',
  porch: 'Eaves overhead. The weather is close.',
  workshop: 'Sawdust and one warm bulb.',
  attic: 'Low beams, old boxes, drifting dust.',
  garage: 'Concrete floor, one buzzing strip light.',
  music: 'Velvet dark and one waiting mic.',
  office: 'A desk lamp and a humming screen.',
  gym: 'Chalk dust and cold air.',
  den: 'Deep couch, hearth still warm.',
  orchard: 'Rows of apple trees and a cider stand.', // 🍎 10-01 orchard line: the first outing had no line ("A quieter corner of the house.")
  lighthouse: 'Spiral stairs, then the lamp room. Quilts on the floor, movie on the screen.', // 🎬 outing (09-19)
  maze: 'Lanterns in the corn. The house is on the hay bales.', // 🌽 09-28 maze outing
};

// 🌙 Night lines (Nina 09-18, all four approved). Only the rooms whose day
// line makes a DAYLIGHT claim get one; the lamplit rooms (dining, library,
// workshop, office, den, music) already read true at any hour. Shown ONLY
// while the room's NIGHT PICTURE is showing (`dark` = roomMasthead().dark),
// never by the clock, so the words and the picture cannot disagree: a room
// with no night take keeps its day line under its day art at midnight.
export const ENTRY_LINES_NIGHT = {
  sunroom: 'Starlight through tall glass.',
  greenhouse: 'Green air, black glass.',
  porch: 'Porch light on. The dark is close.',
  attic: 'Low beams, old boxes, one bare bulb.',
};

export function roomEntryLine(roomId, hour, dark) {
  if (!roomId || roomId === 'house') return null;
  if (dark === true && ENTRY_LINES_NIGHT[roomId]) return ENTRY_LINES_NIGHT[roomId];
  if (roomId === 'roof') {
    const band = bandForHour(hour);
    return band && NIGHT_BANDS.has(band.name)
      ? 'Open sky. The whole night from up here.'
      : 'Open sky above the shingles.';
  }
  return ENTRY_LINES[roomId] || 'A quieter corner of the house.';
}

// ── Tier 0/1: the masthead slot (ROOM_ART_TIER2_SPEC_2026_09_15 §1) ─────────
// The over-films above are capped because they paint ON TOP of the chat.
// The masthead band is different: it is real content (the page layers above
// it by tree order), so it may change OPAQUELY — this is where "stepping
// into another room" actually lives.
//   · indoor rooms  → 'treat': re-aim the house banner art (filter + zoom).
//     ⚠️ LETTERBOX TRAP (07-26 debugging arc): pre-08-01 banners are wide art
//     letterboxed in a 1024-square — objectPosition stays CENTER, movement is
//     scale (cover has already over-cropped; zoom stays inside the art) plus
//     a small translateY. No blur in any treat filter: the wings own the only
//     blur, and animated blur is banned (perf ledger).
//   · outdoor rooms → 'takeover': replace the art with an opaque authored sky
//     (the ruled-but-lost §0 roof takeover, restored 09-15 eve).
// Anti-invariants pinned in roomView.test.js.

// A treat = { filter, scale, translateY } (percent). Filters compose their
// own grade; the render side must not stack the default banner grade under
// them.
const treat = (filter, scale, translateY) => ({ filter, scale, translateY });
const TREATS = {
  dining: (n) => (n
    ? treat('brightness(0.66) sepia(0.24) saturate(1.3)', 1.16, 2)      // pendant light dinner
    : treat('brightness(0.92) sepia(0.12) saturate(1.15)', 1.16, 2)),
  sunroom: (n) => (n
    ? treat('brightness(0.6) saturate(0.85) hue-rotate(200deg) sepia(0.2)', 1.1, -2)
    : treat('brightness(1.08) saturate(1.2) sepia(0.1)', 1.1, -2)),     // glass-bright
  library: (n) => (n
    ? treat('brightness(0.58) sepia(0.32) saturate(1.25) contrast(1.06)', 1.32, 3)
    : treat('brightness(0.74) sepia(0.24) saturate(1.2) contrast(1.04)', 1.32, 3)),
  greenhouse: (n) => (n
    ? treat('brightness(0.55) sepia(0.5) hue-rotate(70deg) saturate(0.9)', 1.2, 1)
    : treat('brightness(0.85) sepia(0.42) hue-rotate(62deg) saturate(1.15)', 1.2, 1)), // green glass
  bathroom: (n) => (n
    ? treat('brightness(0.62) saturate(0.7) hue-rotate(190deg) sepia(0.18)', 1.12, -1)
    : treat('brightness(0.95) saturate(0.82) hue-rotate(168deg) sepia(0.14)', 1.12, -1)), // porcelain cool
  workshop: (n) => (n
    ? treat('brightness(0.6) sepia(0.36) saturate(1.35) hue-rotate(-8deg)', 1.24, 4)
    : treat('brightness(0.78) sepia(0.28) saturate(1.28) hue-rotate(-8deg)', 1.24, 4)),
  attic: (n) => (n
    ? treat('brightness(0.48) saturate(0.62) sepia(0.22) contrast(1.05)', 1.38, -4)
    : treat('brightness(0.62) saturate(0.7) sepia(0.18) contrast(1.05)', 1.38, -4)),   // up under the beams
  garage: (n) => (n
    ? treat('brightness(0.58) saturate(0.68) hue-rotate(16deg) contrast(1.1)', 1.18, 3)
    : treat('brightness(0.74) saturate(0.75) hue-rotate(14deg) contrast(1.08)', 1.18, 3)),
  music: (n) => (n
    ? treat('brightness(0.5) saturate(1.3) hue-rotate(28deg) contrast(1.08)', 1.28, 2)
    : treat('brightness(0.62) saturate(1.22) hue-rotate(24deg) contrast(1.05)', 1.28, 2)), // velvet dark
  gym: (n) => (n
    ? treat('brightness(0.7) saturate(0.72) hue-rotate(10deg)', 1.14, 3)
    : treat('brightness(0.98) saturate(0.8) hue-rotate(8deg) contrast(1.05)', 1.14, 3)),
  office: (n) => (n
    ? treat('brightness(0.6) saturate(0.85) hue-rotate(14deg) sepia(0.1)', 1.2, 1)
    : treat('brightness(0.82) saturate(0.9) hue-rotate(10deg)', 1.2, 1)),
  den: (n) => (n
    ? treat('brightness(0.52) sepia(0.42) saturate(1.45) hue-rotate(-6deg)', 1.26, 4)
    : treat('brightness(0.68) sepia(0.34) saturate(1.35) hue-rotate(-6deg)', 1.26, 4)),  // hearth-warm dark
};
const GENERIC_TREAT = (n) => (n
  ? treat('brightness(0.6) sepia(0.2) saturate(1.1)', 1.15, 1)
  : treat('brightness(0.8) sepia(0.14) saturate(1.05)', 1.15, 1));

// Static star field for the night roof: sparse fixed specks in ONE layer
// (the animated stars stay AmbientLayer's resting look). Deliberately few.
const STAR_FIELD = [
  [12, 22], [28, 61], [41, 15], [55, 44], [67, 26], [81, 58], [92, 18],
].map(([x, y], i) =>
  `radial-gradient(${i % 3 === 0 ? 2 : 1.4}px ${i % 3 === 0 ? 2 : 1.4}px at ${x}% ${y}%, rgba(255,255,255,${i % 2 ? 0.75 : 0.9}), rgba(255,255,255,0) 100%)`
).join(', ');

// Tier 3 "living art" (ROOM_ART_TIER2_SPEC §6): a room_art entry may be an
// object { base, day?, night?, rain? } of https urls. After dark the night
// image wins (09-18); by day wet weather takes the rain image; otherwise the
// band bucket (morning/midday → day; dusk/night →
// night; golden + dawn → base); otherwise base. A junk variant value falls back to
// base; a junk base disqualifies the whole entry (old chain applies).
// Strings stay strings forever — the deployed 12-room map never breaks.
const WET_KINDS = new Set(['rain', 'storm', 'fog', 'snow']);
// 🌦️ Exact weather takes (09-17 late, Nina: the porch shows a sunny day, a
// day with leaves falling, a snowy day, a starry night): a take may be keyed
// by the almanac's weather KIND. The list is the backend vocabulary
// (weatherAlmanac.KINDS minus clear; smoke-house-rebuild greps it for parity).
// Exact kind first, then the generic wet → rain rule (the glass-room
// precedent), then band, then base.
export const WEATHER_TAKE_KINDS = ['rain', 'storm', 'snow', 'fog', 'heat', 'leaves', 'dust', 'bloom'];
const httpsUrl = (v) => (typeof v === 'string' && /^https:\/\//.test(v) ? v : null);

// ── 🍂❄️ Seasons (Tier 4, 09-17 eve — Nina: rooms change with the season;
// plumbing only, images pend her go) ────────────────────────────────────────
// A room_art entry may carry `seasons: { winter|spring|summer|autumn: { base,
// day?, night?, rain? } }` — a PACK per season holding the same three states a
// room has today, so the per-room state count never grows (never the full
// matrix). The season comes from the HOUSE day (clock.day, Phoenix — the
// one-clock doctrine), through the month table the backend almanac owns
// (weatherAlmanac.seasonOf; smoke-house-rebuild greps this table for parity).
export const SEASON_MONTHS = { winter: [12, 1, 2], spring: [3, 4, 5], summer: [6, 7, 8], autumn: [9, 10, 11] };
export const SEASONS = Object.keys(SEASON_MONTHS);
export function seasonForDay(dayKey) {
  if (typeof dayKey !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return null;
  const m = Number(dayKey.slice(5, 7));
  return SEASONS.find((s) => SEASON_MONTHS[s].includes(m)) || null;
}

function resolveRoomArt(entry, band, weatherKind, season) {
  if (typeof entry === 'string') return entry || null;
  if (!entry || typeof entry !== 'object') return null;
  if (!httpsUrl(entry.base)) return null;
  // 🍂❄️ the season PACK wins whole: a state missing from the pack falls to
  // the PACK base, never to the top-level take — one crossfade must never mix
  // a fall window with a summer one. No pack (or a baseless one) = today.
  const sp = season && entry.seasons && typeof entry.seasons === 'object' ? entry.seasons[season] : null;
  const pack = (sp && typeof sp === 'object' && httpsUrl(sp.base)) ? sp : entry;
  const base = httpsUrl(pack.base);
  // 🌙 09-18 (Nina's ruling): AFTER DARK THE NIGHT PICTURE WINS over weather.
  // Every weather take is painted in daylight, so rain-beats-night showed a
  // grey afternoon storm sky at 10pm (unnoticed until a four-day wet run,
  // 09-20..23). The particle layer still draws the rain/snow over it. A room
  // with no night picture keeps its weather take — wet beats a dry base.
  if (NIGHT_BANDS.has(band)) {
    const dark = httpsUrl(pack.night);
    if (dark) return dark;
  }
  if (weatherKind && WEATHER_TAKE_KINDS.includes(weatherKind)) {
    const exact = httpsUrl(pack[weatherKind]);
    if (exact) return exact;
  }
  if (weatherKind && WET_KINDS.has(weatherKind)) {
    const rain = httpsUrl(pack.rain);
    if (rain) return rain;
  }
  // 09-17: `night` means DARK OUTSIDE — dusk + night only (19:00–05:00). The
  // night takes paint a starry sky in every window; golden hour (16-19) and
  // dawn (5-8) showing stars was wrong, so those bands rest on base (its
  // soft window glow reads as either).
  const bucket = DAYLIGHT_BANDS.has(band) ? httpsUrl(pack.day)
    : NIGHT_BANDS.has(band) ? httpsUrl(pack.night) : null;
  return bucket || base;
}

// 🌙 Is this url the pack's NIGHT picture? The reading lane keys off the
// picture that is actually SHOWING, never the clock: a room with no night take
// shows its bright day art at midnight and needs the day lane there.
function isNightTake(entry, season, url) {
  if (!entry || typeof entry !== 'object' || !url) return false;
  const sp = season && entry.seasons && typeof entry.seasons === 'object' ? entry.seasons[season] : null;
  const pack = (sp && typeof sp === 'object' && httpsUrl(sp.base)) ? sp : entry;
  return httpsUrl(pack.night) === url && url !== httpsUrl(pack.base);
}

/** Masthead descriptor for a room, or null (house/null = banner untouched —
 *  the null path must stay byte-identical for every non-camera space).
 *  artMap = the payload's `house.room_art` (Tier 2, data-gated rider):
 *  real art wins the whole chain — art → takeover (outdoor) → treat.
 *  weatherKind = the payload's `house.weather.kind` (absent = clear).
 *  Art is deliberately band-inert; the washes/films/veil carry the hour —
 *  except where a Tier 3 variant exists (day/night/rain), which fixes only
 *  what layers can't: a lit lantern at noon, dry glass in a storm. */
export function roomMasthead(roomId, hour, artMap, weatherKind, season) {
  if (!roomId || roomId === 'house') return null;
  const band = (bandForHour(hour) || bandForHour(21)).name;
  const entry = artMap && typeof artMap === 'object' ? artMap[roomId] : null;
  const art = resolveRoomArt(entry, band, weatherKind, season || null);
  // `dark` rides ONLY on a night take (key absent otherwise, so every
  // non-night descriptor stays byte-identical): the reading lane keys off it.
  if (art) return isNightTake(entry, season || null, art) ? { kind: 'art', url: art, dark: true } : { kind: 'art', url: art };
  const night = NIGHT_BANDS.has(band);
  if (OUTDOORS.has(roomId)) {
    const [top, bottom] = BAND_SKY[band] || BAND_SKY.dusk;
    if (roomId === 'roof') {
      return {
        kind: 'takeover',
        layers: [
          `linear-gradient(180deg, ${top}, ${bottom})`,                       // open sky, opaque
          ...(night ? [STAR_FIELD] : []),
          ...(EVENING_BANDS.has(band)
            ? [`linear-gradient(0deg, rgba(232,169,78,0.35), rgba(232,169,78,0) 22%)`] // town lights below
            : [`linear-gradient(0deg, rgba(255,255,255,0.18), rgba(255,255,255,0) 30%)`]), // daylight haze at the eaves
        ],
      };
    }
    // porch: sky seen from UNDER the eave — wood overhead, posts at the edges
    return {
      kind: 'takeover',
      layers: [
        `linear-gradient(180deg, ${top}, ${bottom})`,
        `linear-gradient(180deg, ${EAVE} 0%, ${EAVE} 14%, rgba(20,13,6,0.85) 18%, rgba(20,13,6,0) 30%)`,
        `linear-gradient(90deg, ${EAVE} 0%, ${EAVE} 2.5%, rgba(20,13,6,0) 6%), linear-gradient(270deg, ${EAVE} 0%, ${EAVE} 2.5%, rgba(20,13,6,0) 6%)`,
        ...(night ? [`linear-gradient(0deg, rgba(255,180,94,0.28), rgba(255,180,94,0) 26%)`] : []), // the porch light
      ],
    };
  }
  return { kind: 'treat', ...(TREATS[roomId] || GENERIC_TREAT)(night) };
}

// ── The wallpaper veil (the ONE true behind-content layer) ──────────────────
// The body wallpaper is the only paint genuinely under the page (everything
// else — including z:-1 — lands over it; see the stacking note up top). The
// injector composes this veil UNDER the content as the first background
// layer. ⚠️ The wallpaper tiles at 500px with a single background-size, so
// the veil MUST be flat (two identical stops) or it would repeat as stripes —
// pinned in roomView.test.js.
const VEILS = {
  dining:     { day: ['42,26,10', 0.30],    night: ['42,26,10', 0.42] },
  sunroom:    { day: ['242,222,160', 0.14], night: ['20,24,48', 0.35] },
  library:    { day: ['30,18,6', 0.42],     night: ['30,18,6', 0.52] },
  greenhouse: { day: ['22,48,28', 0.30],    night: ['22,48,28', 0.45] },
  bathroom:   { day: ['110,168,184', 0.18], night: ['10,20,26', 0.40] },
  porch:      { day: ['130,168,200', 0.22], night: ['20,26,52', 0.45] },
  attic:      { day: ['20,12,6', 0.45],     night: ['20,12,6', 0.55] },
  roof:       { day: ['127,176,221', 0.20], night: ['11,16,38', 0.55] },
  workshop:   { day: ['46,28,10', 0.32],    night: ['46,28,10', 0.45] },
  garage:     { day: ['24,28,34', 0.30],    night: ['24,28,34', 0.45] },
  music:      { day: ['26,13,38', 0.45],    night: ['26,13,38', 0.55] },
  gym:        { day: ['210,205,190', 0.12], night: ['20,24,30', 0.35] },
  office:     { day: ['18,24,40', 0.25],    night: ['18,24,40', 0.40] },
  den:        { day: ['32,16,6', 0.40],     night: ['32,16,6', 0.50] },
};
const GENERIC_VEIL = { day: ['16,12,20', 0.30], night: ['16,12,20', 0.42] };

/** Flat wallpaper veil for a room, or null (house = wallpaper untouched). */
export function roomWallpaperVeil(roomId, hour) {
  if (!roomId || roomId === 'house') return null;
  const band = (bandForHour(hour) || bandForHour(21)).name;
  const [rgb, a] = (VEILS[roomId] || GENERIC_VEIL)[NIGHT_BANDS.has(band) ? 'night' : 'day'];
  const c = `rgba(${rgb},${a})`;
  return `linear-gradient(${c}, ${c})`;
}

// Located events → the room the camera gets invited to (glow + invite, ruled
// 09-15: NEVER auto-move). Sky templates want the roof; harvest wants the
// greenhouse. Fallback chain roof→porch; a house with neither outdoor room
// open simply gets no invite (the event cards still tell the story).
const EVENT_ROOMS = {
  meteor_shower: ['roof', 'porch'],
  comet_watch: ['roof', 'porch'],
  moon_viewing: ['roof', 'porch'],
  blood_moon: ['roof', 'porch'],
  aurora_night: ['roof', 'porch'],
  // 💬 09-26 option C (Nina: "give them scenes"): fireworks joins the sky nights
  // (the house already gathers up there for it: housePlaces.SKY_GATHER_LINES)
  fireworks_night: ['roof', 'porch'],
  harvest: ['greenhouse'],
};

export function eventRoomFor(template, openRoomIds) {
  const chain = EVENT_ROOMS[template];
  if (!chain) return null;
  const open = new Set(Array.isArray(openRoomIds) ? openRoomIds : []);
  for (const id of chain) if (open.has(id)) return id;
  return null;
}

// The invite toast names the event (the generic line undersold the ~09-26
// Harvest Moon). Player copy, rule 9. Unknown templates get no prefix.
const EVENT_PHRASES = {
  meteor_shower: 'A meteor shower tonight.',
  comet_watch: 'A comet tonight.',
  moon_viewing: 'Moon night.',
  blood_moon: 'A blood moon tonight.',
  aurora_night: 'Aurora tonight.',
  fireworks_night: 'Fireworks tonight.', // 💬 09-26 option C: fireworks gets a scene
  harvest: 'Harvest time.',
};

export function eventInvitePhrase(template) {
  return EVENT_PHRASES[template] || null;
}

// 🎪🚪 THE invite resolver (09-17 eve, Nina's go: "every located event
// invites"). Sky/harvest events keep the frontend chain above (which door is
// open decides roof vs porch); every OTHER located event arrives with the
// backend's staging room on its start card (`event_room`, resolved against
// the open set at announce — houseEvents.stagingRoomFor) and the residents'
// own label. Before this, bake-offs, trivia, karaoke, brunch, garage sales
// and the porch open mic all carried a room in the engine and never asked
// anyone to come. A locked or unknown room = no invite (discovery is never
// bypassed by an event). Both composer mounts read ONE result of this.
const INVITE_LABEL_MAX = 48;
export function roomInviteFor(ev, openRoomIds) {
  if (!ev || typeof ev !== 'object' || !Array.isArray(openRoomIds)) return null;
  const template = typeof ev.template === 'string' ? ev.template : null;
  const open = new Set(openRoomIds);
  const room = eventRoomFor(template, openRoomIds)
    || (typeof ev.room === 'string' && open.has(ev.room) ? ev.room : null);
  if (!room) return null;
  let phrase = eventInvitePhrase(template);
  if (!phrase && typeof ev.label === 'string') {
    // player copy (rule 9): sentence case, one period, no em dash, never a wall
    const t = ev.label.replace(/\s*—\s*/g, ' ').replace(/\s+/g, ' ').trim().replace(/[.\s]+$/, '');
    if (t && t.length <= INVITE_LABEL_MAX) phrase = `${t.charAt(0).toUpperCase()}${t.slice(1)}.`;
  }
  return { room, phrase };
}

// 🌕🚪 09-26 (Nina: "the event card has a go button that takes them to the roof
// view"): the Go on an event's START card, or null. The same resolver as the
// invite (roomInviteFor), read against the open rooms the page hands over, so
// the button can never name a room the invite would not. Only a start card, only
// inside the event's 3h life (the card is memoized: an old one keeps its button,
// and the page re-checks the room is still open when it is pressed). An outing's
// card has no room of its own here (its place closes at the wrap), so it gets
// none. `rooms` = the status payload's open rooms [{ id, label }].
export const EVENT_GO_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/** 🔗 09-26 (Nina): a shared room link (`?room=` / `?view=`) lands in the room.
 *  That visit skips the "New here?" tour offer and the quest chrome: the offer
 *  is a full-screen layer that sat on top of the picture the link opened. */
export function isRoomDeepLink(search) {
  try {
    const p = new URLSearchParams(search || '');
    return !!((p.get('room') || '').trim() || (p.get('view') || '').trim());
  } catch (e) { return false; }
}
export function eventGoFor(d, rooms, createdAt, nowMs = Date.now()) {
  if (!d || d.command !== 'house_event' || !d.is_event_start || !Array.isArray(rooms) || !rooms.length) return null;
  const born = new Date(createdAt || 0).getTime();
  if (!(born > 0) || nowMs - born > EVENT_GO_MAX_AGE_MS) return null;
  const inv = roomInviteFor({ template: d.event_template || null, room: d.event_room || null }, rooms.map((r) => r && r.id));
  if (!inv) return null;
  const hit = rooms.find((r) => r && r.id === inv.room);
  const label = (hit && typeof hit.label === 'string' && hit.label) || inv.room;
  return { room: inv.room, text: `Go to ${label} ▶` };
}

// ── 🖼️ Wallpaper art (09-17, Nina: "the room images can barely be seen") ────
// The masthead band is a 300px strip buried under the title, tabs and the top
// of the chat. The body wallpaper is the ONE layer truly behind the page, so
// on desktop the room's art BECOMES the wallpaper: one image, nearly clear in
// the header zone, dimmed from where the chat begins.
//   ⭐⭐ ONE IMAGE, NEVER TWO: when the wallpaper carries the art the masthead
//   band must step aside (bandHidden) — painting both doubles the room over
//   itself at two scales (the 09-17 mockup bug). Pinned in roomView.test.js.
//   ⭐ 09-17/18 HAZE LESSON (Nina: "a bit dim", then "the haze"): the dim was
//   never the whole problem. The room over-films (lamp pools, vignettes) and
//   the day-part wash STACKED on the wallpaper art, and four whisper-films
//   read as fog. With art on the wallpaper the films step aside (the art
//   carries its own lamp) and the wash runs at WASH_OVER_ART.
//   ⭐⭐ THE READING LANE (Nina's pick 09-18, mockup SVwMmkZtxeFWebAnGSRvsY):
//   the dim exists for ONE reason, so the words stay readable, and there are
//   no words in the margins. So the darkness lives only behind the chat
//   column (a feathered lane the page paints), and the margins show the art
//   at full strength. The lane is a CONTRAST FLOOR, not a taste knob, and it
//   keys off the PICTURE SHOWING, never the clock:
//     · night take → LANE_NIGHT 0.35 (measured: every text colour ≥4.5:1)
//     · any daylight picture → LANE_DAY 0.65. Measured on the sun room day
//       art: at 0.35 message text falls under 4.5:1 across 44% of the lane;
//       0.60 is the first value with 0% under. LANE_DAY_FLOOR pins that.
//   Re-measure with a contrast script before moving either number.
// Phones keep the masthead CARD + flat veil (a card and a wallpaper would be
// the same doubling), so callers pass { desktop: false } there.
export const LANE_DAY_FLOOR = 0.6;
export const LANE_DAY = 0.65;        // resting lane under the chat over a daylight picture
export const LANE_NIGHT = 0.35;      // …over a night take (Nina's pick; the art is already dark)
export const LANE_FEATHER = 56;      // px of soft edge OUTSIDE the chat column on each side
export const WALL_DIM_ARRIVE = 0;    // the arrival POSTCARD: the lane clears entirely (0.12 left a faint stripe)
export const WASH_OVER_ART = 0.4;    // day-part wash scale while the wallpaper carries art
/** Resting lane darkness for a masthead descriptor (see the block above). */
export function roomLaneDarkness(masthead) {
  return masthead && masthead.kind === 'art' && masthead.dark ? LANE_NIGHT : LANE_DAY;
}
const WALL_RGB = '13,9,6';
// The url lands inside a CSS url("…") — refuse anything that could close it.
const cssSafeUrl = (u) => (typeof u === 'string' && /^https:\/\/[^\s"'()\\]+$/.test(u) ? u : null);

/** { masthead, wallpaper, bandHidden } for a room.
 *  wallpaper = null | { url, image, dim } — `image`/`dim` are ready CSS
 *  background layers. `dim` is now ONLY a faint header tint that fades to
 *  clear (the margins show the art at full strength); the darkness under the
 *  words is the READING LANE the page paints: `lane` = its resting alpha,
 *  `laneFill` = its fill, which reads --room-dim-a (so the arrival postcard
 *  can animate one number) with `lane` as the fallback. */
export function roomArtPlan(roomId, hour, artMap, weatherKind, opts) {
  const masthead = roomMasthead(roomId, hour, artMap, weatherKind, opts && opts.season);
  const desktop = !!(opts && opts.desktop);
  const url = desktop && masthead && masthead.kind === 'art' ? cssSafeUrl(masthead.url) : null;
  if (!url) return { masthead, wallpaper: null, bandHidden: false };
  const lane = roomLaneDarkness(masthead);
  return {
    masthead,
    bandHidden: true,
    wallpaper: {
      url,
      image: `url("${url}")`,
      // px stops, not %: the header zone is a fixed ~340px tall on every
      // desktop height. Nearly clear up top (the art is meant to be SEEN
      // there, ≤0.10) and fully clear below: no page-wide dim any more.
      dim: `linear-gradient(180deg, rgba(${WALL_RGB},0.06) 0px, rgba(${WALL_RGB},0.10) 170px, rgba(${WALL_RGB},0) 340px)`,
      lane,
      laneFill: `rgba(${WALL_RGB},var(--room-dim-a, ${lane}))`,
    },
  };
}

// ── 🪧 The room plate (09-17): what stays on screen while you're in a room ──
export const ROOM_ICONS = {
  dining: '🕯️', sunroom: '🌤️', library: '📚', greenhouse: '🌿', bathroom: '🛁',
  porch: '🪑', workshop: '🪚', attic: '📦', garage: '🧰', music: '🎙️',
  office: '🖥️', gym: '🏋️', den: '🛋️', roof: '🌌',
  orchard: '🍎', // 🍎 an OUTING (09-19): a temporary place, open only while the house is out
  lighthouse: '🎬', // 🎬 movie night at the lighthouse (09-19), the second outing
  maze: '🌽', // 🌽 09-28 maze: the corn maze, the third outing (Halloween W2, HOUSE_HALLOWEEN_ENABLED)
};

// Room-flavored shortcuts to commands that ALREADY exist — they only prefill
// the composer; nothing here is a new verb, a new backend path, or a new LLM
// call (till charges apply exactly as if typed). Non-mod commands only; the
// test pins every first word against SLASH_COMMANDS. A trailing space = "you
// finish the thought".
const ROOM_VERBS = {
  dining: ["/imagine tonight's dinner spread"],
  sunroom: ['/scene golden afternoon'],
  library: ['/imagine a book from the shelf', '/note '],
  greenhouse: ['/imagine what is blooming today'],
  bathroom: ['/imagine the goose in the tub'],
  porch: ['/scene '],
  workshop: ['/build ', '/tinker '],
  attic: ['/imagine what is in the old boxes'],
  garage: ['/tinker '],
  music: ['/aux ', '/aux next'],
  office: ['/note '],
  den: ['/watch '],
  roof: ['/scene stargazing'],
};
export function roomVerbs(roomId) {
  if (!roomId || roomId === 'house') return [];
  return ROOM_VERBS[roomId] || [];
}

const LOCATIVE_OPENER = /^(?:deep |out |up |down |back )?(?:in|on|at) the\b/i;

/** Presence sentence for a room: one resident carries their doing-line, a
 *  crowd stays names-only (reads in a glance). here = [{ name, doing }]. */
// ── 🚪✨ The arrival postcard (09-17; lengthened 09-19, Nina: "a little longer,
// and include the 'Marlow is deep in...' text a beat into the transition") ────
// The room's line lands first; WHO IS HERE fades in under it a beat later, so
// the postcard reads as two thoughts and not one paragraph. An empty room says
// so in the plate's own words. Any key or tap ends it early (a regular hopping
// rooms must never be made to wait).
export const ARRIVE_MS = 4500;          // was 2500
export const ARRIVE_WHO_DELAY_MS = 1200;
export const ROOM_EMPTY_LINE = 'Nobody else here right now.';
export function roomArrival(flavor, here) {
  return { line: flavor || '', who: roomPresenceLine(here) || ROOM_EMPTY_LINE };
}

export function roomPresenceLine(here) {
  const list = Array.isArray(here) ? here.filter((h) => h && h.name) : [];
  if (list.length === 0) return null;
  if (list.length === 1) {
    const { name, doing } = list[0];
    // 09-18 (sweep #143 observation): a universal house line IS a place line
    // ("in the greenhouse where it is always summer"), so "is here, in the
    // greenhouse…" said the room twice. A status that opens on its own
    // locative carries the sentence by itself.
    if (doing && LOCATIVE_OPENER.test(doing)) return `${name} is ${doing}.`;
    return `${name} is here${doing ? `, ${doing}` : ''}.`;
  }
  return `${list.map((h) => h.name).join(' and ')} are here.`;
}

// ── 🚶 Presence changes while you sit in a room (09-17 eve, Nina's ruling:
// arrivals AND departures, on the plate) ────────────────────────────────────
// Statuses re-derive hourly; the page keeps the last snapshot and this diffs
// it against the new one for the room you're in. Snapshots are
// { [id]: { name, place } } (place null = an identity line, nowhere in
// particular). The verb varies per name but never re-rolls (same name, same
// verb — reload-stable, like everything else in the house). Player copy,
// rule 9: short, no em dash.
const HOUSE_PLACE_LABELS = { kitchen: 'the kitchen', couch: 'the couch', 'their room': 'their room', porch: 'the porch' };
const ARRIVE_VERBS = ['wanders in', 'drifts in', 'comes through the door', 'slips in'];
const LEAVE_VERBS = ['drifts off toward', 'heads for', 'slips out toward', 'wanders off to'];
const nameHash = (n) => String(n || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
function placeLabel(place, rooms) {
  const r = (Array.isArray(rooms) ? rooms : []).find((x) => x && x.place === place);
  return (r && r.label) || HOUSE_PLACE_LABELS[place] || null;
}
export function presenceDelta(prev, next, roomId, rooms) {
  if (!prev || !next || typeof prev !== 'object' || typeof next !== 'object') return [];
  if (!roomId || roomId === 'house') return [];
  const rc = (Array.isArray(rooms) ? rooms : []).find((r) => r && r.id === roomId);
  const here = rc && rc.place;
  if (!here) return [];
  const lines = [];
  for (const [id, n] of Object.entries(next)) {
    if (!n || n.place !== here) continue;
    const p = prev[id];
    if (!p || p.place !== here) lines.push(`${n.name} ${ARRIVE_VERBS[nameHash(n.name) % ARRIVE_VERBS.length]}.`);
  }
  for (const [id, p] of Object.entries(prev)) {
    if (!p || p.place !== here) continue;
    const n = next[id];
    if (n && n.place === here) continue;
    const dest = n && n.place ? placeLabel(n.place, rooms) : null;
    lines.push(dest
      ? `${p.name} ${LEAVE_VERBS[nameHash(p.name) % LEAVE_VERBS.length]} ${dest}.`
      : `${p.name} drifts off.`);
  }
  return lines;
}

/** 👁 09-23 (Nina: option A): the /look content as STRUCTURE, for the merged
 *  /look picture (RoomPeekLightbox's look column). roomLookText joins exactly
 *  these parts, so the text fallback and the column can never drift.
 *  Presence is deliberately NOT here — the lightbox owns the per-person
 *  lines (peekCaption), one owner. Player copy, rule 9. */
export function roomLookParts({ roomId, label, hour, others, weatherLabel, tvOn, dark }) {
  const inHouse = !roomId || roomId === 'house';
  const n = Math.max(0, Number(others) || 0);
  return {
    name: inHouse ? 'The main room' : (label ? label.charAt(0).toUpperCase() + label.slice(1) : 'This room'),
    flavor: inHouse ? null : roomEntryLine(roomId, hour, dark),
    tv: !!(inHouse && tvOn),
    weather: weatherLabel ? `${weatherLabel.charAt(0).toUpperCase()}${weatherLabel.slice(1)} outside.` : null,
    watchers: n > 0 ? `${n} other${n === 1 ? ' is' : 's are'} looking in.` : null,
    // (the "🔒 Down the hall, X is still locked." tease left 09-24, Nina: the
    // same line in every room)
  };
}

/** /look — a private, client-built, ZERO-LLM description of the room you're
 *  in. Everything comes from the payload already held. Watcher counts live
 *  HERE, not on the plate (Nina 09-17). Since the 09-23 merge this joined
 *  form survives as the no-picture fallback (the main room, a bot, a room
 *  with no art yet); the picture path renders roomLookParts beside the art. */
export function roomLookText({ roomId, label, hour, here, others, weatherLabel, tvOn, dark }) {
  const p = roomLookParts({ roomId, label, hour, others, weatherLabel, tvOn, dark });
  const lines = [[`${p.name}.`, p.flavor].filter(Boolean).join(' ')];
  lines.push(roomPresenceLine(here) || 'Nobody else is here right now.');
  if (p.tv) lines.push('The TV is on.');
  const tail = [p.weather, p.watchers].filter(Boolean).join(' ');
  if (tail) lines.push(tail);
  return lines.join('\n');
}

// ── 🚪🖼️ The rooms picker (09-18; contract = mockup v12, Nina's 09-17
// rulings) ───────────────────────────────────────────────────────────────────
// TWO doors over ONE model: typing "/room " lists ROWS in the composer palette
// (tap = go); the DOOR in the message box / a bare "/room" send opens the PICTURES
// as a popup over the chat. Everything here is pure and comes from the status
// payload already held — zero requests, zero LLM.
//   ⭐⭐ NO locked/undiscovered room, in either door: this model only ever
//   reads `rooms` (the OPEN set) and drops anything flagged otherwise. A card
//   appears when a door opens (/look's locked-room tease was retired 09-24).
//   ⭐⭐ NOTHING IS DRAWN OVER A PICTURE: `art` carries pixels only
//   ({kind,url|layers|filter}); every word sits in fields the component
//   renders BENEATH the image. (The one ruled exception is the plain 📍 "you"
//   pin, which the component owns — it is a marker, not a label.)
//   ⭐⭐ Marks are TEXT (`weather.label`, "📺 on"): the fog emoji has no glyph
//   on Nina's Mac (a gray box), so pictographs are stripped from the label.
// Pinned in roomViewPicker.test.js.
const PICTOGRAPHS = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu;
const markText = (label) => {
  const t = typeof label === 'string' ? label.replace(PICTOGRAPHS, '').replace(/\s+/g, ' ').trim() : '';
  return t || null;
};

export function roomPickerEntries(args) {
  const {
    rooms, viewerRoom, hour, artMap, weatherKind, weatherLabel, season,
    tvOn, bannerUrl, presenceFor, watchers, inviteRoom, bookOpen,
    heat, peopleLive, // 👥 10-01 people
  } = (args && typeof args === 'object') ? args : {};
  const heatMap = (heat && typeof heat === 'object') ? heat : {}; // 👥 10-01 people
  const open = (Array.isArray(rooms) ? rooms : []).filter((r) => r && typeof r === 'object'
    && typeof r.id === 'string' && r.id && r.id !== 'house'
    && r.locked !== true && r.discovered !== false);
  const here = viewerRoom && (viewerRoom === 'house' || open.some((r) => r.id === viewerRoom)) ? viewerRoom : 'house';
  const banner = httpsUrl(bannerUrl);
  const counts = (watchers && typeof watchers === 'object') ? watchers : {};
  const wet = weatherKind && weatherKind !== 'clear' ? markText(weatherLabel) : null;
  const facesOf = (id) => {
    let list = null;
    try { list = typeof presenceFor === 'function' ? presenceFor(id) : null; } catch (e) { list = null; }
    return (Array.isArray(list) ? list : []).filter((h) => h && h.name)
      .map((h) => ({ name: String(h.name), avatar: httpsUrl(h.avatar), ...(h.person === true ? { person: true } : {}) })); // 👥 10-01 people
  };
  const artOf = (id) => {
    if (id === 'house') return banner ? { kind: 'image', url: banner } : null;
    const mh = roomMasthead(id, hour, artMap, weatherKind, season || null);
    if (mh && mh.kind === 'art') return { kind: 'image', url: mh.url };
    if (mh && mh.kind === 'takeover') return { kind: 'layers', layers: mh.layers.slice() };
    if (mh && mh.kind === 'treat' && banner) return { kind: 'treated', url: banner, filter: mh.filter };
    return null;
  };
  return [{ id: 'house', label: 'main room' }, ...open].map((r) => {
    const you = r.id === here;
    // 🍎 a running outing always glows as an invitation: it is gone tonight
    const glow = !you && ((!!inviteRoom && r.id === inviteRoom) || r.outing === true);
    const faces = facesOf(r.id); // 👥 10-01 people
    return {
      id: r.id,
      label: typeof r.label === 'string' && r.label ? r.label : r.id,
      icon: r.id === 'house' ? '🕯️' : (ROOM_ICONS[r.id] || '🚪'),
      main: r.id === 'house',
      you,
      glow, // 👥 10-01 people: computed above (the heat reads it)
      art: artOf(r.id),
      // 📚 the House library (HOUSE_LIBRARY_SPEC §3.1): "📖 open" on the library while a book is open
      mark: r.id === 'house' ? (tvOn ? '📺 on' : null) : (r.outing === true ? 'today only' : ((r.id === 'library' && bookOpen === true) ? '📖 open' : (OUTDOORS.has(r.id) ? wet : null))),
      faces,
      // 👥 10-01 people (R13 'Glow, people + AIs'): the server's level, the purple invite wins, never on your own card
      heat: (!you && !glow) ? Math.max(0, Math.min(3, Math.floor(Number(heatMap[r.id]) || 0))) : 0,
      people: faces.filter((f) => f.person).length, // 👥 10-01 people
      watching: peopleLive ? 0 : Math.max(0, (Number(counts[r.id]) || 0) - (you ? 1 : 0)), // 👥 10-01 people: rows count people when the payload has them
    };
  });
}

/** The LIST under a committed "/room " (composer palette). Rows keep the
 *  watcher count (Nina: list rows yes, cards no). */
export function roomPickerRows(args) {
  return roomPickerEntries(args).map((e) => ({
    ...e,
    sub: e.faces.length ? e.faces.map((f) => f.name).join(', ') : 'quiet',
  }));
}

/** The PICTURES popup. No watcher count, ever; main room double-wide first;
 *  the caption name drops its article ("library", not "the library"). */
export function roomPickerCards(args) {
  return roomPickerEntries(args).map(({ watching, people, label, ...e }) => ({ // 👥 10-01 people: + people stripped
    ...e,
    name: label.replace(/^the /i, ''),
    span: e.main ? 2 : 1,
  }));
}
